<template>
    <div>
        <b-card no-body class="mt-5 no-shadow border-0">
            <b-card-body class="pt-4">
                <div>
                    <b-button variant="success" class="mb-2" @click="openBookModal">
                        <b-icon icon="plus"></b-icon> เพิ่มหนังสือ
                    </b-button>
                    <span v-if="courseBooksList.length" class="ml-2">
                        มีทั้งหมด {{ courseBooksList.length }} เล่ม
                    </span>
                    <span v-else class="ml-2">
                        ไม่มีหนังสือประกอบการเรียน
                    </span>
                </div>

                <div v-if="courseBooksList.length" class="mt-3 d-lg-flex">
                    <draggable v-model="courseBooksList" animation="150" handle=".book-handle-wrapper" @end="reorderBooks"
                        :class="['book-list-column pr-lg-3', { disabled: isLoading }]" ghost-class="ghost">
                        <b-card v-for="book in courseBooksList" :key="book.cb_id" no-body
                            class="mb-2 no-shadow book-item-card">
                            <b-card-body class="py-3 px-3">
                                <b-media no-body>
                                    <b-media-aside vertical-align="top" class="mr-3">
                                        <div class="book-handle-wrapper pointer">
                                            <img src="/images/icons/icon_movable.png" alt="movable" height="20"
                                                class="align-middle" />
                                        </div>
                                    </b-media-aside>
                                    <b-media-aside vertical-align="center" class="mr-3 border-left pl-2">
                                        <div class="book-cover-wrapper">
                                            <b-img v-if="book.book.image_url" :src="book.book.image_url" fluid alt="book cover"
                                                class="book-cover-image" />
                                            <div v-else
                                                class="book-cover-placeholder d-flex align-items-center justify-content-center">
                                                <b-icon-image class="text-muted"></b-icon-image>
                                            </div>
                                        </div>
                                    </b-media-aside>
                                    <b-media-body>
                                        <b-row no-gutters align-v="center">
                                            <b-col>
                                                <div class="pl-3">
                                                    <a href="#" class="book-name-link" @click.prevent>
                                                        <u>{{ formatBookLabel(book.book) }}</u>
                                                    </a>
                                                </div>
                                            </b-col>
                                            <b-col cols="auto">
                                                <b-button size="sm" variant="outline-danger" @click="removeBook(book)">
                                                    <i class="fa fa-trash" aria-hidden="true"></i> ลบ
                                                </b-button>
                                            </b-col>
                                        </b-row>
                                    </b-media-body>
                                </b-media>
                            </b-card-body>
                        </b-card>
                    </draggable>
                </div>
            </b-card-body>
        </b-card>

        <b-modal v-model="isModalVisible" title="เพิ่มหนังสือ" centered hide-footer size="lg" @hidden="clearSelectedBooks">
            <b-form-group label="ชื่อหนังสือ" label-class="font-weight-bold">
                <template>
                    <v-select v-model="selectedBooks" :options="selectableBooks" label="book_name"
                        :reduce="book => book" placeholder="เลือกหนังสือ" multiple :close-on-select="false"
                        class="mb-3" :loading="isLoadingBooks" :disabled="isLoadingBooks">
                        <template #option="{ book_code, book_name, book_id }">
                            <div style="display:flex; gap:8px; align-items:center;"
                                @click="handleDeselect(isSelected(book_id), book_id)">
                                <b-form-checkbox :checked="isSelected(book_id)" />
                                {{ book_code }} {{ book_name }}
                            </div>
                        </template>
                        <template #selected-option="{ book_code, book_name }">
                            {{ book_code }} {{ book_name }}
                        </template>
                        <template #no-options>
                            <span>ไม่พบหนังสือ</span>
                        </template>
                    </v-select>
                </template>
            </b-form-group>

            <div class="d-flex justify-content-end mt-4">
                <b-button variant="primary" :disabled="isLoadingBooks || isLoading || !selectedBooks.length"
                    @click="saveSelectedBooks">
                    บันทึก
                </b-button>
            </div>
        </b-modal>
    </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import draggable from "csq-vue-multi-draggable";

export default {
    name: "BookManager",
    components: {
        vSelect,
        draggable
    },
    computed: {
        ...mapState('CourseBookManagement', ['availableBooks']),
        ...mapGetters('CourseBookManagement', ['isLoading', 'isLoadingBooks']),
        selectableBooks() {
            const addedBookIds = new Set(
                this.courseBooksList.map(courseBook => courseBook.book.book_id)
            );
            return this.availableBooks.filter(book => !addedBookIds.has(book.book_id));
        },
    },
    data() {
        return {
            courseId: this.$route.params.courseId,
            isModalVisible: false,
            courseBooksList: [],
            selectedBooks: [],
            isLoading: false,
        };
    },
    async mounted() {
        await this.getCourseBooks();
    },
    methods: {
        ...mapActions('CourseBookManagement', [
            'fetchAvailableBooks',
            'fetchCourseBooks',
            'addBooksToCourse',
            'removeBookFromCourse',
            'reorderCourseBooks'
        ]),
        ...mapActions("CourseManagement", [
            "getCourseInfo",
        ]),
        async refreshCourseInfo() {
            try {
                await this.getCourseInfo({
                    courseId: this.courseId,
                    temp: true,
                    excepts: 'numberOfStudent,numberOfActiveStudent',
                });
            } catch (error) {
                console.error("Failed to refresh course info:", error);
            }
        },
        showToast(message, title = "Success", variant = "success", autoHideDelay = 2000) {
            this.$bvToast.toast(message, {
                title,
                variant,
                toaster: "b-toaster-top-right",
                autoHideDelay,
                appendToast: true,
                noHoverPause: true,
                noCloseButton: true,
            });
        },
        clearSelectedBooks() {
            this.selectedBooks = [];
        },
        isSelected(bookId) {
            return this.selectedBooks.some((b) => b.book_id === bookId);
        },
        handleDeselect(isSelected, bookId) {
            if (!isSelected) {
                return;
            }
            setTimeout(() => {
                this.selectedBooks = this.selectedBooks.filter((b) => b.book_id !== bookId);
            }, 0);
        },
        formatBookLabel(book) {
            if (!book) {
                return "";
            }

            if (book.book_code) {
                return `${book.book_code} ${book.book_name}`;
            }

            return book.book_name || "";
        },
        async reorderBooks() {
            try {
                await this.reorderCourseBooks({ courseId: this.courseId, books: this.courseBooksList });
                await this.refreshCourseInfo();
                this.showToast("จัดลำดับหนังสือสำเร็จ");
            } catch (error) {
                this.showToast("ไม่สามารถจัดลำดับหนังสือได้", "Error", "danger", 3000);
            }
        },
        async getCourseBooks() {
            try {
                this.courseBooksList = await this.fetchCourseBooks(this.courseId);
            } catch (error) {
                this.showToast("ไม่สามารถดึงข้อมูลหนังสือได้", "Error", "danger", 3000);
            }
        },
        async openBookModal() {
            if (!this.availableBooks.length) {
                await this.fetchAvailableBooks();
            }
            this.selectedBooks = [];
            this.isModalVisible = true;
            await this.fetchAvailableBooks();
            this.syncSelectedBooks();
        },

        async saveSelectedBooks() {
            try {
                const existingBookIds = this.courseBooksList.map((courseBook) => courseBook.book.book_id);
                const selectedBookIds = this.selectedBooks.map((book) => book.book_id);
                const bookIds = [...new Set([...existingBookIds, ...selectedBookIds])];
                await this.addBooksToCourse({ courseId: this.courseId, bookIds });
                
                await this.getCourseBooks();
                await this.refreshCourseInfo();
                
                this.showToast("บันทึกรายการหนังสือสำเร็จ");
                this.isModalVisible = false;
            } catch (error) {
                this.showToast("ไม่สามารถบันทึกหนังสือได้", "Error", "danger", 3000);
            }
        },
        async removeBook(book) {
            this.$bvModal
                .msgBoxConfirm(
                    `คุณต้องการลบหนังสือ ${this.formatBookLabel(book.book)} ใช่หรือไม่?`,
                    {
                        title: "แจ้งเตือนการลบหนังสือ",
                        buttonSize: "sm",
                        okTitle: "ลบหนังสือ",
                        okVariant: "primary",
                        cancelTitle: "ยกเลิก",
                        cancelVariant: "outline-primary",
                        hideHeaderClose: false,
                        headerCloseVariant: "white",
                        headerCloseContent: "&times;",
                        headerBgVariant: "danger",
                        headerClass: "py-2 px-3 border-0",
                        footerClass: "p-3 border-0",
                        centered: true,
                    }
                )
                .then(async (confirm) => {
                    if (!confirm) {
                        return;
                    }

                    try {
                        await this.removeBookFromCourse({ courseId: this.courseId, bookId: book.book.book_id });
                        this.courseBooksList = this.courseBooksList.filter(
                            (item) => item.book.book_id !== book.book.book_id
                        );

                        await this.refreshCourseInfo();
                        this.showToast("ลบหนังสือสำเร็จ");
                    } catch (error) {
                        this.showToast("ไม่สามารถลบหนังสือได้", "Error", "danger", 3000);
                    }
                });
        },
    },
};
</script>

<style scoped>
.book-list-column {
    flex: 1 1 auto;
    min-width: 0;
}

.book-list-column.disabled {
    opacity: 0.5;
    pointer-events: none;
    position: relative;
}

.book-list-column.disabled::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.3);
    z-index: 10;
    cursor: not-allowed;
}


.book-item-card {
    border: 1px solid #e9ecef;
}

.book-handle-wrapper {
    width: 20px;
}

.book-cover-wrapper {
    width: 56px;
    height: 80px;
    overflow: hidden;
    border: 1px solid #e9ecef;
    background-color: #f8f9fa;
}

.book-cover-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.book-cover-placeholder {
    width: 100%;
    height: 100%;
}

.book-name-link {
    color: #007bff;
}

.book-loading-indicator {
    position: absolute;
    right: 24px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 5;
}

@media (max-width: 991.98px) {
    .book-list-column {
        width: 100%;
        flex: 1 1 auto;
    }
}

::v-deep .v-select,
::v-deep .v-select .vs__dropdown-toggle {
    width: 100%;
    max-width: 100%;
    min-width: 0;
}

::v-deep .v-select .vs__dropdown-toggle {
    height: auto;
    min-height: 32px;
}

::v-deep .v-select .vs__selected-options {
    flex: 1 1 auto;
    flex-wrap: wrap;
    min-width: 0;
    max-width: 100%;
}

::v-deep .v-select .vs__selected {
    max-width: 100%;
    overflow: visible;
    text-overflow: ellipsis;
    white-space: wrap;
}

::v-deep .v-select .vs__deselect {
    flex-shrink: 0;
}

::v-deep .v-select .vs__search {
    min-width: 0;
    flex: 1 1 auto;
}
</style>
